<template>
    <div height="medium" width="large">
        <div>
            <h2 class="heading">Record Requests Inbox</h2>
        </div>

        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>

        <div class="status-grid">
            <!-- Pending -->
            <button type="button" class="status-card" @click="selectStatus('pending')">
                <div class="status-header">
                    <span class="status-badge status-badge--pending">
                        <common-icon name="timer" size="tiny"></common-icon>
                        Pending
                    </span>
                </div>

                <p class="status-count">{{ statusCounts.pending }}</p>
                <p class="status-label">
                    {{ statusCounts.pending === 1 ? 'request' : 'requests' }}
                </p>
            </button>

            <!-- Responded -->
            <button type="button" class="status-card" @click="selectStatus('responded')">
                <div class="status-header">
                    <span class="status-badge status-badge--responded">
                        <common-icon name="check" size="tiny"></common-icon>
                        Responded
                    </span>
                </div>

                <p class="status-count">{{ statusCounts.responded }}</p>
                <p class="status-label">
                    {{ statusCounts.responded === 1 ? 'request' : 'requests' }}
                </p>
            </button>

            <!-- Declined -->
            <button type="button" class="status-card" @click="selectStatus('declined')">
                <div class="status-header">
                    <span class="status-badge status-badge--declined">
                        <common-icon name="curcle" size="tiny"></common-icon>
                        Declined
                    </span>
                </div>

                <p class="status-count">{{ statusCounts.declined }}</p>
                <p class="status-label">
                    {{ statusCounts.declined === 1 ? 'request' : 'requests' }}
                </p>
            </button>
        </div>

        <!-- Pending request alert -->
        <div v-if="statusCounts.pending > 0" class="pending-alert" role="alert">
            <common-icon name="timer" size="small" class="pending-alert__icon"></common-icon>

            <div class="pending-alert__content">
                <strong>
                    {{ statusCounts.pending }}
                    {{ statusCounts.pending === 1 ? 'request' : 'requests' }}
                </strong>
                awaiting your response. Review and respond within the requested timeframe to maintain compliance.
            </div>
        </div>

        <!-- Incoming Record Requests -->
        <div class="requests-card">
            <div class="requests-card__header">
                <div class="requests-card__title">
                    <common-icon name="sms" size="small"></common-icon>
                    <h3>Incoming Record Requests</h3>
                </div>

                <div class="request-filters">
                    <button
                        type="button"
                        :class="['filter-button', { 'filter-button--active': selectedFilter === 'all' }]"
                        @click="selectedFilter = 'all'"
                    >
                        All ({{ recordRequests.length }})
                    </button>

                    <button
                        type="button"
                        :class="['filter-button', { 'filter-button--active': selectedFilter === 'pending' }]"
                        @click="selectedFilter = 'pending'"
                    >
                        Pending ({{ getStatusCount('pending') }})
                    </button>

                    <button
                        type="button"
                        :class="['filter-button', { 'filter-button--active': selectedFilter === 'responded' }]"
                        @click="selectedFilter = 'responded'"
                    >
                        Responded ({{ getStatusCount('responded') }})
                    </button>

                    <button
                        type="button"
                        :class="['filter-button', { 'filter-button--active': selectedFilter === 'declined' }]"
                        @click="selectedFilter = 'declined'"
                    >
                        Declined ({{ getStatusCount('declined') }})
                    </button>
                </div>
            </div>

            <div class="requests-card__content">
                <div
                    v-for="request in filteredRequests"
                    :key="request.id"
                    :class="[
                        'request-item',
                        {
                            'request-item--pending': request.status === 'pending',
                        },
                    ]"
                >
                    <div class="request-item__main">
                        <div class="request-item__details">
                            <!-- Hospital + status + priority -->
                            <div class="request-item__top">
                                <span class="request-provider">
                                    {{ request.provider }}
                                </span>

                                <span :class="['request-status', `request-status--${request.status}`]">
                                    <common-icon :name="getStatusIcon(request.status)" size="tiny"></common-icon>

                                    {{ formatStatus(request.status) }}
                                </span>

                                <span :class="['priority-badge', `priority-badge--${request.priority}`]">
                                    {{ request.priority }}
                                </span>

                                <span v-if="request.source" class="source-badge">
                                    <common-icon name="users" size="tiny"></common-icon>

                                    {{ request.source }}
                                </span>
                            </div>

                            <!-- Patient information -->
                            <p class="request-meta">
                                <span>Patient:</span>
                                {{ request.patient }}

                                <span class="separator">•</span>

                                <span>Request ID:</span>
                                {{ request.id }}

                                <span class="separator">•</span>

                                <span>Received:</span>
                                {{ request.received }}

                                <span v-if="request.due" class="due-date">• Due: {{ request.due }}</span>
                            </p>

                            <!-- Requested records -->
                            <div class="record-badges">
                                <span v-for="record in request.records" :key="record" class="record-badge">
                                    {{ record }}
                                </span>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="request-actions">
                            <button type="button" class="expand-button" @click="toggleRequest(request.id)">
                                <common-icon
                                    name="dropdown"
                                    size="small"
                                    :class="{
                                        'expand-icon--open': expandedRequest === request.id,
                                    }"
                                ></common-icon>
                            </button>

                            <button
                                v-if="request.status === 'pending'"
                                type="button"
                                class="respond-button"
                                @click="respondToRequest(request)"
                            >
                                <common-icon name="send" size="small"></common-icon>
                                Respond
                            </button>

                            <button
                                v-if="request.status === 'responded'"
                                type="button"
                                class="view-button"
                                @click="viewRequest(request)"
                            >
                                <common-icon name="eye" size="semi-small"></common-icon>
                                View
                            </button>
                        </div>
                    </div>

                    <!-- Expanded content -->
                    <div v-if="expandedRequest === request.id" class="request-expanded">
                        <div class="request-details-grid">
                            <!-- Purpose -->
                            <div class="request-detail">
                                <span class="request-detail__label">Purpose</span>
                                <span class="request-detail__value">
                                    {{ request.purpose }}
                                </span>
                            </div>

                            <!-- HIPAA Authorization -->
                            <div class="request-detail">
                                <span class="request-detail__label">HIPAA Authorization</span>

                                <span
                                    v-if="request.hipaaAuthorization"
                                    class="request-detail__value request-detail__value--success"
                                >
                                    <common-icon name="insure" size="semi-small"></common-icon>
                                    Attached
                                </span>

                                <span v-else class="request-detail__value request-detail__value--error">Not attached</span>
                            </div>

                            <!-- Date Range -->
                            <div v-if="request.dateRange" class="request-detail">
                                <span class="request-detail__label">Date Range Requested</span>

                                <span class="request-detail__value">
                                    {{ request.dateRange }}
                                </span>
                            </div>

                            <!-- Requesting Provider Contact -->
                            <div class="request-detail">
                                <span class="request-detail__label">Requesting Provider Contact</span>

                                <div class="provider-contact">
                                    <div v-if="request.contact?.email" class="provider-contact__item">
                                        <common-icon name="sms" size="tiny"></common-icon>
                                        {{ request.contact.email }}
                                    </div>

                                    <div v-if="request.contact?.phone" class="provider-contact__item">
                                        <common-icon name="phone" size="tiny"></common-icon>
                                        {{ request.contact.phone }}
                                    </div>

                                    <div v-if="request.contact?.fax" class="provider-contact__item">
                                        <common-icon name="print" size="tiny"></common-icon>
                                        {{ request.contact.fax }}
                                    </div>
                                </div>
                            </div>

                            <!-- Response Note -->
                            <div v-if="request.responseNote" class="request-detail request-detail--full">
                                <span class="request-detail__label">Response Note</span>

                                <span class="request-detail__value request-detail__value--italic">
                                    {{ request.responseNote }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data() {
            return {
                notificationMessage: '',

                statusCounts: {
                    pending: 1,
                    responded: 1,
                    declined: 1,
                },

                selectedFilter: 'all',
                expandedRequest: null,

                recordRequests: [
                    {
                        id: 'INC001',
                        provider: 'Northeast Florida Medical Center',
                        patient: 'Amos David',
                        status: 'pending',
                        priority: 'urgent',
                        received: '2/10/2025',
                        due: '2/17/2025',
                        source: null,
                        records: ['Lab Results', 'Consultation Notes'],
                        purpose: 'Specialist Referral',
                        hipaaAuthorization: true,
                        dateRange: '6/1/2024 → 1/1/2025',

                        contact: {
                            email: 'medicalrecords@nfmc.com',
                            phone: '(904) 953-2000',
                            fax: '(904) 953-2099',
                        },

                        responseNote: null,
                    },
                    {
                        id: 'INC002',
                        provider: 'Mayo Clinic Jacksonville',
                        patient: 'Amos David',
                        status: 'responded',
                        priority: 'routine',
                        received: '1/28/2025',
                        due: null,
                        source: 'Via Network',
                        records: ['Complete Medical History', 'Imaging Reports'],
                        purpose: 'Second Opinion',
                        hipaaAuthorization: true,

                        contact: {
                            email: 'records@mayo.edu',
                            phone: '(904) 953-2000',
                            fax: '(904) 953-7799',
                        },

                        responseNote: 'Records uploaded via Medmatch Network portal.',
                    },
                    {
                        id: 'INC003',
                        provider: "St. Vincent's Medical Center",
                        patient: 'Amos David',
                        status: 'declined',
                        priority: 'routine',
                        received: '1/20/2025',
                        due: null,
                        source: null,
                        records: ['Mental Health Records'],
                        purpose: 'Continuing Care',
                        hipaaAuthorization: false,

                        contact: {
                            email: 'him@jaxhealth.com',
                            phone: '(904) 308-7300',
                            fax: '(904) 308-7399',
                        },

                        responseNote: 'Patient authorization does not cover mental health records for this provider.',
                    },
                ],
            }
        },

        computed: {
            filteredRequests() {
                if (this.selectedFilter === 'all') {
                    return this.recordRequests
                }

                return this.recordRequests.filter((request) => request.status === this.selectedFilter)
            },
        },

        methods: {
            selectStatus(status) {
                this.selectedFilter = status
                this.$emit('status-selected', status)
            },

            getStatusCount(status) {
                return this.recordRequests.filter((request) => request.status === status).length
            },

            formatStatus(status) {
                return status.charAt(0).toUpperCase() + status.slice(1)
            },

            getStatusIcon(status) {
                const icons = {
                    pending: 'timer',
                    responded: 'check',
                    declined: 'curcle',
                }

                return icons[status]
            },

            toggleRequest(requestId) {
                this.expandedRequest = this.expandedRequest === requestId ? null : requestId
            },

            respondToRequest(request) {
                this.$emit('respond-request', request)
            },

            viewRequest(request) {
                this.$emit('view-request', request)
            },
        },
    }
</script>

<style lang="css" scoped>
    .heading {
        font-size: 16px;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
        margin-bottom: 14px;
    }

    .status-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
    }

    .status-card {
        width: 100%;
        padding: 16px;
        text-align: left;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        cursor: pointer;
        transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
    }

    .status-card:hover {
        background-color: #f9fafb;
        border-color: #d1d5db;
        box-shadow: 0 1px 3px rgb(0 0 0 / 5%);
    }

    .status-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;
    }

    .status-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        border-radius: 9999px;
        font-size: 12px;
        line-height: 18px;
        font-weight: 500;
    }

    .status-badge--pending {
        color: #92400e;
        background-color: #fef3c7;
    }

    .status-badge--responded {
        color: #166534;
        background-color: #dcfce7;
    }

    .status-badge--declined {
        color: #991b1b;
        background-color: #fee2e2;
    }

    .status-count {
        margin: 0;
        color: #111827;
        font-size: 24px;
        line-height: 32px;
        font-weight: 700;
    }

    .status-label {
        margin: 0;
        color: #6b7280;
        font-size: 12px;
        line-height: 18px;
    }

    @media (max-width: 768px) {
        .status-grid {
            grid-template-columns: 1fr;
        }
    }

    .pending-alert {
        width: 100%;
        display: grid;
        grid-template-columns: 16px 1fr;
        column-gap: 12px;
        align-items: start;
        margin-top: 16px;
        padding: 12px 16px;
        border: 1px solid #fcd34d;
        border-radius: 8px;
        background-color: #fefce8;
        color: #713f12;
        font-size: 14px;
        line-height: 20px;
    }

    .pending-alert__icon {
        color: #ca8a04;
        margin-top: 2px;
    }

    .pending-alert__content {
        display: grid;
        gap: 4px;
    }

    .pending-alert__content strong {
        font-weight: 600;
    }
    .requests-card {
        margin-top: 16px;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        overflow: hidden;
    }

    .requests-card__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 16px 24px 12px;
    }

    .requests-card__title {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .requests-card__title h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 400;
        color: #111827;
    }

    .request-filters {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .filter-button {
        height: 32px;
        padding: 0 12px;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: #374151;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
    }

    .filter-button:hover {
        background: #f3f4f6;
    }

    .filter-button--active {
        background: #2563eb;
        color: #ffffff;
    }

    .filter-button--active:hover {
        background: #1d4ed8;
    }

    .requests-card__content {
        padding: 16px 24px 24px;
    }

    .request-item {
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #ffffff;
        transition: border-color 0.2s ease;
        padding: 16px;
    }

    .request-item + .request-item {
        margin-top: 12px;
    }

    .request-item--pending {
        border-color: #fde68a;
        background: rgba(254, 252, 232, 0.3);
    }

    .request-item__main {
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    .request-item__details {
        flex: 1;
        min-width: 0;
    }

    .request-item__top {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin-bottom: 6px;
    }

    .request-provider {
        color: #111827;
        font-size: 14px;
        font-weight: 600;
    }

    .request-status {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        border-radius: 9999px;
        font-size: 12px;
        line-height: 18px;
        font-weight: 500;
    }

    .request-status--pending {
        color: #92400e;
        background: #fef3c7;
    }

    .request-status--responded {
        color: #166534;
        background: #dcfce7;
    }

    .request-status--declined {
        color: #991b1b;
        background: #fee2e2;
    }

    .priority-badge {
        padding: 2px 8px;
        border-radius: 9999px;
        background: #f3f4f6;
        color: #374151;
        font-size: 12px;
        font-weight: 500;
        text-transform: capitalize;
    }

    .priority-badge--urgent {
        color: #92400e;
        background: #fef3c7;
    }

    .source-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        border: 1px solid #bbf7d0;
        border-radius: 9999px;
        background: #f0fdf4;
        color: #15803d;
        font-size: 12px;
        font-weight: 500;
    }

    .request-meta {
        margin: 0 0 8px;
        color: #6b7280;
        font-size: 12px;
        line-height: 18px;
    }

    .request-meta span {
        color: #374151;
        font-weight: 500;
    }

    .request-meta .separator {
        color: #9ca3af;
        font-weight: 400;
        margin: 0 3px;
    }

    .request-meta .due-date {
        color: #b45309;
        font-weight: 500;
    }

    .record-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
    }

    .record-badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 6px;
        background: #f3f4f6;
        color: #374151;
        font-size: 12px;
        font-weight: 500;
    }

    .request-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }

    .expand-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 6px;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: #9ca3af;
        cursor: pointer;
    }

    .expand-button:hover {
        color: #374151;
        background: #f3f4f6;
    }

    .expand-button :deep(.expand-icon--open) {
        transform: rotate(180deg);
    }

    .respond-button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 32px;
        padding: 0 12px;
        border: 0;
        border-radius: 6px;
        background: #2563eb;
        color: #ffffff;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
    }

    .respond-button:hover {
        background: #1d4ed8;
    }

    .view-button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 32px;
        padding: 0 12px;
        border: 1px solid #86efac;
        border-radius: 6px;
        background: #ffffff;
        color: #15803d;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
    }

    .view-button:hover {
        background: #f0fdf4;
    }

    .request-expanded {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #e5e7eb;
    }

    .request-details-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 32px;
        row-gap: 12px;
        font-size: 14px;
    }

    .request-detail {
        min-width: 0;
    }

    .request-detail--full {
        grid-column: span 2;
    }

    .request-detail__label {
        display: block;
        margin-bottom: 2px;
        color: #6b7280;
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 0.05em;
        text-transform: uppercase;
    }

    .request-detail__value {
        display: flex;
        align-items: center;
        gap: 4px;
        color: #374151;
        font-size: 14px;
    }

    .request-detail__value--success {
        color: #15803d;
    }

    .request-detail__value--error {
        color: #dc2626;
    }

    .request-detail__value--italic {
        font-style: italic;
    }

    .provider-contact {
        display: flex;
        flex-direction: column;
        gap: 2px;
        color: #374151;
    }

    .provider-contact__item {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .provider-contact__item :deep(.common-icon) {
        color: #9ca3af;
    }

    @media (max-width: 900px) {
        .requests-card__header {
            align-items: flex-start;
            flex-direction: column;
        }

        .request-filters {
            flex-wrap: wrap;
        }
    }

    @media (max-width: 768px) {
        .request-item__main {
            flex-direction: column;
        }

        .request-actions {
            width: 100%;
            justify-content: flex-end;
        }
    }
</style>
